import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { adminFetch } from '../hooks/useAdminFetch'

interface OverviewData {
  total_tenants: number
  active_tenants: number
  trial_tenants: number
  inactive_tenants: number
  uploads_24h: number
  uploads_7d: number
  uploads_30d: number
  ingestion_error_rate: number
  tenants_created_last_30d: { date: string; count: number }[]
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string
  value: number | string
  sub?: string
  color?: string
}) {
  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
    >
      <div className="text-xs font-medium mb-2" style={{ color: 'var(--km-text-muted)' }}>
        {label}
      </div>
      <div
        className="text-3xl font-bold"
        style={{ color: color ?? 'var(--km-text-primary)' }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: 'var(--km-text-muted)' }}>
          {sub}
        </div>
      )}
    </div>
  )
}

export function AdminHome() {
  const { data, isLoading, error } = useQuery<OverviewData>({
    queryKey: ['admin', 'overview'],
    queryFn: () => adminFetch('/api/v1/admin/overview'),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="text-sm" style={{ color: 'var(--km-text-muted)' }}>
        Caricamento statistiche...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-sm" style={{ color: 'var(--km-error)' }}>
        Errore nel caricamento dei dati. Verificare il token admin.
      </div>
    )
  }

  const errorColor =
    data.ingestion_error_rate > 10
      ? 'var(--km-error)'
      : data.ingestion_error_rate > 5
      ? 'var(--km-warning)'
      : 'var(--km-success)'

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
        Overview piattaforma
      </h2>

      {/* Tenant KPI cards */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--km-text-muted)' }}>
          Tenant
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Totale tenant" value={data.total_tenants} />
          <StatCard
            label="Attivi (14gg)"
            value={data.active_tenants}
            color="var(--km-success)"
          />
          <StatCard
            label="Trial"
            value={data.trial_tenants}
            color="var(--km-warning)"
          />
          <StatCard
            label="Inattivi"
            value={data.inactive_tenants}
            color="var(--km-text-muted)"
          />
        </div>
      </div>

      {/* Upload stats */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--km-text-muted)' }}>
          Upload
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Ultimi 24h" value={data.uploads_24h} />
          <StatCard label="Ultimi 7 giorni" value={data.uploads_7d} />
          <StatCard label="Ultimi 30 giorni" value={data.uploads_30d} />
          <StatCard
            label="Tasso errore (30gg)"
            value={`${data.ingestion_error_rate}%`}
            color={errorColor}
          />
        </div>
      </div>

      {/* Trend chart */}
      <div
        className="rounded-xl border p-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--km-text-primary)' }}>
          Nuovi tenant — ultimi 30 giorni
        </h3>
        {data.tenants_created_last_30d.length === 0 ? (
          <div className="text-sm text-center py-8" style={{ color: 'var(--km-text-muted)' }}>
            Nessun tenant creato negli ultimi 30 giorni
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={data.tenants_created_last_30d}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fill: 'var(--km-text-muted)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                tickFormatter={(v: string) => v.slice(5)} // mostra solo MM-DD
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: 'var(--km-text-muted)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={24}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--km-surface-2)',
                  border: '1px solid var(--km-border)',
                  borderRadius: 8,
                  color: 'var(--km-text-primary)',
                  fontSize: 12,
                }}
                cursor={{ fill: 'rgba(99,102,241,0.07)' }}
              />
              <Bar dataKey="count" fill="var(--km-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
